import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  BoxProps,
  Circle,
  Center,
  Flex,
  Grid,
  GridItem,
  Spacer,
  Text,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import {
  borrowingBaseSummaryFormatterMap,
  BorrowingBaseSummaryValueType,
} from '~/packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

type TopLevelStatRowProps = {
  initial: React.ReactNode;
  proposed: React.ReactNode;
  label: React.ReactNode;
};

export const TopLevelStatRow = ({
  label,
  initial,
  proposed,
}: TopLevelStatRowProps) => {
  return (
    <Grid templateColumns="repeat(4, 1fr)" templateRows="repeat(1, 56px)">
      <GridItem
        colSpan={2}
        color={colors.aurora[900]}
        borderBottom="2px solid"
        borderColor={colors.aurora[900]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
      >
        {label}
      </GridItem>
      <GridItem
        colSpan={1}
        borderBottom="2px solid"
        borderColor={colors.aurora[900]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
      >
        {initial}
      </GridItem>
      <GridItem
        colSpan={1}
        alignSelf="end"
        bg={colors.aurora[900]}
        height="64px"
      >
        <Flex height="100%" color="white" p="16px" alignItems="center">
          {proposed}
        </Flex>
      </GridItem>
    </Grid>
  );
};

type StatRowProps = {
  initial: React.ReactNode;
  proposed: React.ReactNode;
  label: string;
  calcTerminologiesLabel?: string;
  hasAccordionPanel: boolean;
  isExpanded: boolean;
  noBackground?: boolean;
};

export const StatRow = ({
  label,
  initial,
  proposed,
  calcTerminologiesLabel,
  hasAccordionPanel,
  isExpanded,
  noBackground = false,
}: StatRowProps) => {
  return (
    <Grid
      templateColumns="repeat(4, 1fr)"
      templateRows="repeat(1, 56px)"
      width="100%"
      mt="16px"
    >
      <GridItem
        colSpan={2}
        color={colors.black[95]}
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
      >
        <Flex alignItems="center">
          <Text fontWeight="semibold">{label}</Text>
          {calcTerminologiesLabel && (
            <Box display="inline-block" ml="8px">
              <CalculationTerminologiesPopover
                termKeyLabel={calcTerminologiesLabel}
              />
            </Box>
          )}
        </Flex>
      </GridItem>
      <GridItem
        colSpan={1}
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
        color={colors.black[95]}
      >
        {initial}
      </GridItem>
      <GridItem
        colSpan={1}
        alignSelf="end"
        bg={noBackground ? 'none' : colors.aurora[900]}
        height="64px"
        borderBottom={noBackground ? '1px solid' : 'none'}
        borderColor={noBackground ? colors.gray[200] : 'none'}
      >
        <Flex height="100%" color="white" p="16px" alignItems="center">
          <>
            {proposed}
            {hasAccordionPanel && (
              <>
                <Spacer />
                <Icon
                  icon={
                    isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                  }
                  size="large"
                  color={noBackground ? colors.aurora[400] : colors.white}
                />
              </>
            )}
          </>
        </Flex>
      </GridItem>
    </Grid>
  );
};

export const DollarWithCircleIcon = (props: BoxProps) => (
  <Circle border="2px solid" size="20px" alignSelf="center" {...props}>
    <Center>
      <Text fontSize="smaller" fontWeight="bold">
        $
      </Text>
    </Center>
  </Circle>
);

type AssignmentSummaryAccordionProps = {
  accordionButton: (props: {
    hasAccordionPanel: boolean;
    isExpanded: boolean;
  }) => React.ReactElement;
  accordionPanel?: React.ReactElement;
  defaultIsExpanded?: boolean;
  isTopLevel?: boolean;
};
export const AssignmentSummaryAccordion = ({
  accordionButton,
  accordionPanel,
  defaultIsExpanded,
  isTopLevel = false,
}: AssignmentSummaryAccordionProps) => {
  const hasAccordionPanel = !!accordionPanel;

  return (
    <Accordion allowMultiple defaultIndex={defaultIsExpanded ? [0] : undefined}>
      <AccordionItem isDisabled={!hasAccordionPanel} border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton
              paddingInline="0"
              paddingBlock="0"
              _disabled={{ color: 'black', cursor: 'default' }}
              marginTop={isTopLevel ? '24px' : '0px'}
              _focus={{ border: 'none' }}
              _hover={{ background: 'inherit' }}
            >
              {accordionButton({ hasAccordionPanel, isExpanded })}
            </AccordionButton>
            <AccordionPanel padding="0px">{accordionPanel}</AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

type AssignmentSummaryAccordionRowProps = {
  label?: string;
  subLabel?: string;
  calcTerminologiesLabel?: string;
  initialValue?: Util.Maybe<number | string>;
  proposedValue?: Util.Maybe<number | string>;
  isSecondLevelAccordion?: boolean; // true denotes an accordion that is indented
  valueType: BorrowingBaseSummaryValueType;
  hasAccordionPanel?: boolean;
  isExpanded?: boolean;
  fullRowBackground?: boolean;
  background: 'light' | 'medium';
  level: number;
};
export const AssignmentSummaryAccordionRow = ({
  label,
  subLabel,
  calcTerminologiesLabel,
  initialValue,
  proposedValue,
  isSecondLevelAccordion,
  valueType,
  hasAccordionPanel = false,
  isExpanded = false,
  fullRowBackground = false,
  background,
  level,
}: AssignmentSummaryAccordionRowProps) => {
  const formattedInitialValue = borrowingBaseSummaryFormatterMap[valueType](
    initialValue,
  );
  const formattedProposedValue = borrowingBaseSummaryFormatterMap[valueType](
    proposedValue,
  );

  // level represents indentation, level 1 is handled by a separate component
  const rowLabelMargin = () => {
    switch (level) {
      case 2:
        return '52px';
      case 3:
        return '68px';
      case 4:
        return '84px';
      default:
        return finleyAssertUnreachable(level as never);
    }
  };

  const backgroundColor =
    background === 'light' ? colors.gray[50] : colors.aurora[50];

  return (
    <Box width="100%">
      <Grid minH="48px" templateColumns="repeat(4, 1fr)">
        <GridItem
          colSpan={2}
          backgroundColor={fullRowBackground ? backgroundColor : undefined}
          padding="8px 16px"
          display="flex"
          alignItems="center"
          ml={rowLabelMargin()}
          borderBottom="1px"
          borderBottomColor={colors.gray[200]}
        >
          <Text
            textAlign="left"
            whiteSpace="pre-line"
            fontWeight={subLabel || isSecondLevelAccordion ? 400 : 600}
            color={
              subLabel || isSecondLevelAccordion
                ? colors.black[70]
                : colors.black[95]
            }
          >
            {label || subLabel}
          </Text>
          {calcTerminologiesLabel && (
            <Box display="inline-block" ml="8px">
              <CalculationTerminologiesPopover
                termKeyLabel={calcTerminologiesLabel}
              />
            </Box>
          )}
        </GridItem>

        <GridItem
          colSpan={1}
          padding="8px 16px"
          backgroundColor={fullRowBackground ? backgroundColor : undefined}
          display="flex"
          alignItems="center"
          borderBottom={level > 1 ? '1px' : '0px'}
          borderBottomColor={colors.gray[200]}
        >
          <Text fontWeight={500} textAlign="left" color={colors.black[95]}>
            {formattedInitialValue}
          </Text>
        </GridItem>
        <GridItem
          colSpan={1}
          padding="8px 16px"
          backgroundColor={level < 4 ? backgroundColor : undefined}
          display="flex"
          alignItems="center"
          borderBottom={level > 1 ? '1px' : '0px'}
          borderBottomColor={colors.gray[200]}
        >
          <Text fontWeight={500} textAlign="left" color={colors.black[95]}>
            {formattedProposedValue}
          </Text>
          <Spacer />
          {hasAccordionPanel && (
            <Icon
              icon={isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'}
              size="large"
              color={colors.aurora[400]}
            />
          )}
        </GridItem>
      </Grid>
    </Box>
  );
};
